import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Avatar from 'components/common/Avatar';
import ErrorBoundary from 'components/common/ErrorBoundary';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import ComentariosSoporte from './ComentariosSoporte';
import InfiniteScroll from "react-infinite-scroll-component";
import { FiCheck } from 'react-icons/fi';
import Skeleton from 'react-loading-skeleton'; // Asegúrate de tener instalada esta librería

const SoporteTecnico = ({
    agrupacionLicencias = [],
    setHabilitarBtnCombinar,
    setShowModalCombinarClientes,
    idClienteAkasia,
    emailCliente
}) => {
    const [problemaSoporte, setProblemaSoporte] = useState('');
    const { token, setToken, profileImage } = useToken();
    const { showErrorToast } = useToast();
    const [comentarios, setComentarios] = useState([]);

    // Estados para el scroll infinito y carga inicial
    const [paginaLogs, setPaginaLogs] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [cargando, setCargando] = useState(true); // <-- Estado para controlar el Skeleton

    const HORAS_ESPERA = 3;

    // Efecto para simular o disparar la carga inicial de comentarios al montar el componente
    useEffect(() => {
        if (idClienteAkasia) {
            setCargando(true);
            setPaginaLogs(1);
            setHasMore(true);
            // Aquí llamarías a tu carga inicial. Para este ejemplo, simulamos que termina de cargar:
            fetchMoreComments();
        }
    }, [idClienteAkasia]);

    // Función para cargar más comentarios paginados
    const fetchMoreComments = async () => {
        try {
            const response = await fetch('/api/cliente/ObtenerComentariosSoporte', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    idClienteAkasia: idClienteAkasia,
                    pagina: paginaLogs,
                    tamanioPagina: 10
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles);
                    const nuevosComentarios = parsed.table2;

                    if (nuevosComentarios && nuevosComentarios.length > 0) {
                        setComentarios(prev => [...prev, ...nuevosComentarios]);
                        setPaginaLogs(prev => prev + 1);

                        if (nuevosComentarios.length < 10) {
                            setHasMore(false);
                        }
                    } else {
                        setHasMore(false);
                    }
                } else {
                    setHasMore(false);
                }
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error al paginar comentarios de soporte:", error);
            setHasMore(false);
        } finally {
            setCargando(false); // <-- Apaga el Skeleton al terminar la petición
        }
    };

    const crearComentarioSoporte = async () => {
        if (problemaSoporte.trim().length < 15) {
            showErrorToast('El problema debe tener al menos 15 caracteres.');
            return;
        }

        var jDataSend = {};
        jDataSend.vProblema = problemaSoporte;
        jDataSend.idClienteAkasia = idClienteAkasia;

        const response = await fetch("/api/cliente/RegistrarProblemaSoporte/", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(jDataSend)
        }).catch((error) => {
            showErrorToast('Hubo un problema con la peticion Fetch:' + error);
            console.log('Hubo un problema con la peticion Fetch:' + error);
        });

        if (response && response.ok) {
            const data = await response.json();

            if (data.iResponseType == 1) {
                var detalles = JSON.parse(data.detalles);
                const comentario = detalles.table2[0];
                setComentarios([comentario, ...comentarios]);
                setProblemaSoporte('');
            } else if (data.iResponseType == 3) {
                showErrorToast(data.vMessage);
            } else {
                showErrorToast(data.vMessage);
                setHabilitarBtnCombinar(false);
                setShowModalCombinarClientes(false);
            }
        } else if (response) {
            showErrorToast(response.status + ' ' + response.statusText);
            setHabilitarBtnCombinar(false);
            setShowModalCombinarClientes(false);

            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }
    };

    const LogSkeleton = () => (
        <div style={{ padding: 0 }}>
            {[1, 2, 3].map((i) => (
                <div
                    key={i}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        padding: "12px 16px",
                        borderBottom: "1px solid #e3e3e3",
                    }}
                >
                    <div className="d-flex align-items-center mb-2">
                        <Skeleton circle height={32} width={32} className="me-2" />
                        <Skeleton height={16} width="40%" />
                    </div>
                    <div style={{ paddingLeft: 40 }}>
                        <Skeleton height={14} width="90%" className="mb-1" />
                        <Skeleton height={12} width="30%" />
                    </div>
                </div>
            ))}
        </div>
    );

    if (agrupacionLicencias.length === 0) return null;

    return (
        <Row className="g-3 mb-3">
            <Col className="col-12">
                <Card className="mb-1 shadow-sm border-0">
                    <Card.Header className="border-bottom bg-light">
                        <h5 className="mb-0 fw-bold">Soporte T&eacute;cnico</h5>
                    </Card.Header>

                    <Card.Body className="p-3">
                        {/* El Formulario se queda fijo arriba */}
                        <Form id="comment" className="mb-3" onSubmit={(e) => {
                            e.preventDefault();
                            crearComentarioSoporte();
                        }}>
                            <div className="d-flex align-items-start mb-2">
                                <Avatar src={profileImage} size="xl" />
                                <Form.Control
                                    type="text"
                                    className="ms-2"
                                    placeholder="Describe un nuevo problema..."
                                    rows={3}
                                    value={problemaSoporte}
                                    style={{ resize: "none" }}
                                    maxLength={999}
                                    minLength={15}
                                    onChange={e => setProblemaSoporte(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (e.target.value.length >= 15) {
                                                crearComentarioSoporte();
                                            }
                                        }
                                    }}
                                />
                            </div>

                            {problemaSoporte.length > 0 && problemaSoporte.length < 15 && (
                                <small className="text-muted ms-5 pt-1 d-block">
                                    M&iacute;nimo 15 caracteres ({problemaSoporte.length}/15)
                                </small>
                            )}
                        </Form>

                        <hr className="text-200 my-3" />
                        <div
                            id="scrollableTargetComentarios"
                            style={{ maxHeight: '350px', overflow: 'auto' }}
                        >
                            {cargando ? (
                                <LogSkeleton />
                            ) : (
                                <InfiniteScroll
                                    dataLength={comentarios.length}
                                    next={fetchMoreComments}
                                    hasMore={hasMore}
                                    loader={
                                        <div className="d-flex justify-content-center align-items-center py-3">
                                            <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                                            <span className="text-muted" style={{ fontSize: '0.85rem' }}>Cargando m&aacute;s registros...</span>
                                        </div>
                                    }
                                    scrollableTarget="scrollableTargetComentarios"
                                    scrollThreshold={0.95}
                                    endMessage={
                                        comentarios.length > 0 && (
                                            <div className="text-center py-3 border-top-0">
                                                <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                    <FiCheck className="me-1" size={12} />
                                                    No hay m&aacute;s comentarios
                                                </small>
                                            </div>
                                        )
                                    }
                                    >
                                        <ErrorBoundary>
                                        <ComentariosSoporte
                                            idClienteAkasia={idClienteAkasia}
                                            comentarios={comentarios}
                                            setComentarios={setComentarios}
                                            emailCliente={emailCliente}
                                        />
                                    </ErrorBoundary>
                                </InfiniteScroll>
                            )}
                        </div>
                    </Card.Body>

                    <Card.Footer className="border-top text-end bg-light">
                        <small className="text-muted">
                            <FontAwesomeIcon icon="info-circle" className="me-1" />
                            Puedes crear un ticket de soporte cada {HORAS_ESPERA} horas
                        </small>
                    </Card.Footer>
                </Card>
            </Col>
        </Row>
    );
};

SoporteTecnico.propTypes = {
    agrupacionLicencias: PropTypes.array,
    setHabilitarBtnCombinar: PropTypes.func,
    setShowModalCombinarClientes: PropTypes.func,
    idClienteAkasia: PropTypes.string,
    emailCliente: PropTypes.string
};

export default SoporteTecnico;